import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, User, Phone, Mail, Briefcase } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TechniciansPage = ({ getAuthHeaders }) => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialty: '',
    can_login: false,
    password: ''
  });

  const loadTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians?active_only=false`, getAuthHeaders());
      setTechnicians(response.data);
    } catch (error) {
      console.error('Error loading technicians:', error);
      toast.error("Erreur lors du chargement des techniciens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTechnicians();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTech) {
        await axios.patch(`${API}/technicians/${editingTech.id}`, formData, getAuthHeaders());
        toast.success("Technicien mis à jour");
      } else {
        await axios.post(`${API}/technicians`, formData, getAuthHeaders());
        toast.success("Technicien ajouté");
      }
      setShowDialog(false);
      resetForm();
      loadTechnicians();
    } catch (error) {
      console.error('Error saving technician:', error);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (tech) => {
    setEditingTech(tech);
    setFormData({
      name: tech.name,
      email: tech.email,
      phone: tech.phone,
      specialty: tech.specialty,
      can_login: tech.can_login,
      password: ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (techId) => {
    if (!window.confirm('Désactiver ce technicien?')) return;
    
    try {
      await axios.delete(`${API}/technicians/${techId}`, getAuthHeaders());
      toast.success("Technicien désactivé");
      loadTechnicians();
    } catch (error) {
      console.error('Error deleting technician:', error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      specialty: '',
      can_login: false,
      password: ''
    });
    setEditingTech(null);
  };

  const specialtyColors = {
    'Matériel': 'bg-blue-100 text-blue-800',
    'Logiciel': 'bg-purple-100 text-purple-800',
    'Réseau': 'bg-green-100 text-green-800',
    'Sécurité': 'bg-red-100 text-red-800',
    'Généraliste': 'bg-gray-100 text-gray-800'
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="text-gray-600">Chargement...</div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des Techniciens</h2>
        <Button
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
          className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un Technicien
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {technicians.map((tech) => (
          <Card key={tech.id} className={`hover:shadow-lg transition-shadow ${!tech.active ? 'opacity-50' : ''}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {tech.name.charAt(0)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{tech.name}</CardTitle>
                    <Badge className={specialtyColors[tech.specialty] || 'bg-gray-100 text-gray-800'}>
                      {tech.specialty}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span>{tech.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{tech.phone}</span>
                </div>
                {tech.can_login && (
                  <Badge variant="outline" className="text-xs">
                    Accès CRM
                  </Badge>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(tech)}
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(tech.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {technicians.length === 0 && (
        <Card className="p-12">
          <div className="text-center text-gray-500">
            <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun technicien. Ajoutez-en un pour commencer.</p>
          </div>
        </Card>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTech ? 'Modifier' : 'Ajouter'} un Technicien</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                disabled={!!editingTech}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="specialty">Spécialité *</Label>
              <Select
                value={formData.specialty}
                onValueChange={(value) => setFormData({...formData, specialty: value})}
                required
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Matériel">Matériel</SelectItem>
                  <SelectItem value="Logiciel">Logiciel</SelectItem>
                  <SelectItem value="Réseau">Réseau</SelectItem>
                  <SelectItem value="Sécurité">Sécurité</SelectItem>
                  <SelectItem value="Généraliste">Généraliste</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!editingTech && (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="can_login"
                    checked={formData.can_login}
                    onChange={(e) => setFormData({...formData, can_login: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="can_login" className="cursor-pointer">
                    Permettre l'accès au CRM
                  </Label>
                </div>

                {formData.can_login && (
                  <div>
                    <Label htmlFor="password">Mot de passe *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={formData.can_login}
                      className="mt-2"
                      placeholder="Minimum 8 caractères"
                    />
                  </div>
                )}
              </>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                {editingTech ? 'Mettre à jour' : 'Ajouter'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
              >
                Annuler
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
